// Comprehensive User Demographics Collection
// Collects appropriate user demographic and technical information for analytics

export interface UserDemographics {
  // Device Information
  device_type: 'mobile' | 'tablet' | 'desktop';
  screen_width: number;
  screen_height: number;
  screen_resolution: string;
  viewport_width: number;
  viewport_height: number;
  pixel_ratio: number;
  
  // Browser/System Information
  browser_name: string;
  browser_version: string;
  os_name: string;
  os_version: string;
  user_agent: string;
  language: string;
  languages: string[];
  
  // Geographic/Locale Information
  timezone: string;
  timezone_offset: number;
  currency?: string;
  
  // Capabilities
  touch_support: boolean;
  cookies_enabled: boolean;
  local_storage_enabled: boolean;
  online: boolean;
  
  // Preferences
  color_scheme: 'light' | 'dark' | 'no-preference';
  reduced_motion: boolean;
  
  // Technical Details
  memory?: number;
  connection_type?: string;
  connection_downlink?: number;
  connection_rtt?: number;
  
  // Session Context
  referrer: string;
  entry_point: string;
  is_new_session: boolean;
}

/**
 * Detects device type based on screen size and user agent
 */
function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const width = window.innerWidth;
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check for specific mobile/tablet indicators in user agent
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  const isTablet = /ipad|tablet|playbook|silk/i.test(userAgent) || 
                   (isMobile && width >= 768);
  
  if (isTablet) return 'tablet';
  if (isMobile || width < 768) return 'mobile';
  return 'desktop';
}

/**
 * Parses user agent to extract browser information
 */
function getBrowserInfo(): { name: string; version: string } {
  const userAgent = navigator.userAgent;
  
  // Chrome
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    const match = userAgent.match(/Chrome\/(\d+)/);
    return { name: 'Chrome', version: match ? match[1] : 'unknown' };
  }
  
  // Firefox
  if (userAgent.includes('Firefox')) {
    const match = userAgent.match(/Firefox\/(\d+)/);
    return { name: 'Firefox', version: match ? match[1] : 'unknown' };
  }
  
  // Safari
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    const match = userAgent.match(/Version\/(\d+)/);
    return { name: 'Safari', version: match ? match[1] : 'unknown' };
  }
  
  // Edge
  if (userAgent.includes('Edg')) {
    const match = userAgent.match(/Edg\/(\d+)/);
    return { name: 'Edge', version: match ? match[1] : 'unknown' };
  }
  
  return { name: 'Unknown', version: 'unknown' };
}

/**
 * Parses user agent to extract OS information
 */
function getOSInfo(): { name: string; version: string } {
  const userAgent = navigator.userAgent;
  
  // Windows
  if (userAgent.includes('Windows NT')) {
    const match = userAgent.match(/Windows NT (\d+\.\d+)/);
    const version = match ? match[1] : 'unknown';
    return { name: 'Windows', version };
  }
  
  // macOS
  if (userAgent.includes('Mac OS X')) {
    const match = userAgent.match(/Mac OS X (\d+[._]\d+)/);
    const version = match ? match[1].replace('_', '.') : 'unknown';
    return { name: 'macOS', version };
  }
  
  // iOS
  if (userAgent.includes('iPhone OS') || userAgent.includes('OS ')) {
    const match = userAgent.match(/OS (\d+[._]\d+)/);
    const version = match ? match[1].replace('_', '.') : 'unknown';
    return { name: 'iOS', version };
  }
  
  // Android
  if (userAgent.includes('Android')) {
    const match = userAgent.match(/Android (\d+\.\d+)/);
    const version = match ? match[1] : 'unknown';
    return { name: 'Android', version };
  }
  
  // Linux
  if (userAgent.includes('Linux')) {
    return { name: 'Linux', version: 'unknown' };
  }
  
  return { name: 'Unknown', version: 'unknown' };
}

/**
 * Checks if local storage is available
 */
function isLocalStorageEnabled(): boolean {
  try {
    const testKey = '__test_localStorage__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Gets network connection information if available
 */
function getConnectionInfo(): {
  type?: string;
  downlink?: number;
  rtt?: number;
} {
  // @ts-expect-error - NetworkInformation is not in TypeScript types yet
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  
  if (connection) {
    return {
      type: connection.effectiveType || connection.type || 'unknown',
      downlink: connection.downlink,
      rtt: connection.rtt
    };
  }
  
  return {};
}

/**
 * Gets device memory if available
 */
function getDeviceMemory(): number | undefined {
  // @ts-expect-error - deviceMemory is not in TypeScript types yet
  return navigator.deviceMemory;
}

/**
 * Gets currency based on locale
 */
function getCurrencyFromLocale(): string | undefined {
  try {
    const locale = navigator.language;
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD' // Default, will be overridden by locale-specific currency
    });
    
    // Try to detect currency from locale
    if (locale.includes('US') || locale.includes('en-US')) return 'USD';
    if (locale.includes('GB') || locale.includes('en-GB')) return 'GBP';
    if (locale.includes('EU') || locale.includes('de') || locale.includes('fr') || locale.includes('es') || locale.includes('it')) return 'EUR';
    if (locale.includes('CA') || locale.includes('en-CA') || locale.includes('fr-CA')) return 'CAD';
    if (locale.includes('AU') || locale.includes('en-AU')) return 'AUD';
    if (locale.includes('JP') || locale.includes('ja')) return 'JPY';
    if (locale.includes('IN') || locale.includes('en-IN')) return 'INR';
    
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Determines if this is a new session
 */
function isNewSession(): boolean {
  const sessionKey = 'galos_session_active';
  const isActive = sessionStorage.getItem(sessionKey) === 'true';
  
  if (!isActive) {
    sessionStorage.setItem(sessionKey, 'true');
    return true;
  }
  
  return false;
}

/**
 * Gets comprehensive user demographics and technical information
 */
export function getUserDemographics(): UserDemographics {
  const browser = getBrowserInfo();
  const os = getOSInfo();
  const connection = getConnectionInfo();
  
  return {
    // Device Information
    device_type: getDeviceType(),
    screen_width: screen.width,
    screen_height: screen.height,
    screen_resolution: `${screen.width}x${screen.height}`,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    pixel_ratio: window.devicePixelRatio || 1,
    
    // Browser/System Information
    browser_name: browser.name,
    browser_version: browser.version,
    os_name: os.name,
    os_version: os.version,
    user_agent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages ? Array.from(navigator.languages) : [navigator.language],
    
    // Geographic/Locale Information
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezone_offset: new Date().getTimezoneOffset(),
    currency: getCurrencyFromLocale(),
    
    // Capabilities
    touch_support: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    cookies_enabled: navigator.cookieEnabled,
    local_storage_enabled: isLocalStorageEnabled(),
    online: navigator.onLine,
    
    // Preferences
    color_scheme: window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 
                 window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'no-preference',
    reduced_motion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false,
    
    // Technical Details
    memory: getDeviceMemory(),
    connection_type: connection.type,
    connection_downlink: connection.downlink,
    connection_rtt: connection.rtt,
    
    // Session Context
    referrer: document.referrer,
    entry_point: window.location.pathname,
    is_new_session: isNewSession(),
  };
}

/**
 * Gets a subset of demographics for user properties (removes technical details).
 */
export function getUserDemographicsForProfile(): Partial<UserDemographics> {
  const full = getUserDemographics();
  
  return {
    // Device & System (user preference indicators)
    device_type: full.device_type,
    screen_resolution: full.screen_resolution,
    browser_name: full.browser_name,
    os_name: full.os_name,
    
    // Geographic/Locale (important for user segmentation)
    timezone: full.timezone,
    language: full.language,
    currency: full.currency,
    
    // Accessibility/Preferences (important for UX)
    color_scheme: full.color_scheme,
    reduced_motion: full.reduced_motion,
    touch_support: full.touch_support,
  };
}
